import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Activity, AlertCircle } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Paginator } from '@/components/ui/paginator';
import type { SiteDetailContext } from './SiteRoute';
import { useSiteEvents } from '@/hooks/useSiteEvents';
import { EventTimeline } from '@/components/sites/EventTimeline';

/**
 * Activity log tab.
 *
 * Renders the audit-events stream for this site as a GitHub-style
 * vertical timeline. Filtering happens server-side via the API
 * `actorType` query param. Pagination is the uniform numbered-page
 * contract shared across every list endpoint.
 *
 * Per-event presentation lives in `eventDescriptors.tsx` — adding a new
 * event type only changes that file.
 */

export const SiteDetailEvents: React.FC = () => {
  const { site } = useOutletContext<SiteDetailContext>();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useSiteEvents(site.id, {
    actorType: 'user',
    page,
  });
  const events = data?.items ?? [];
  const pagination = data?.pagination;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-5 flex-1" />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = (
      <EmptyState
        icon={AlertCircle}
        title="Couldn't load activity"
        description={error instanceof Error ? error.message : 'Failed to load events.'}
      />
    );
  } else if (events.length === 0) {
    body = (
      <EmptyState
        icon={Activity}
        title="No activity yet"
        description="Events for this site will appear here as they happen."
      />
    );
  } else {
    body = (
      <>
        <EventTimeline events={events} />
        <Paginator
          pagination={pagination}
          onPageChange={setPage}
          className="pt-4"
        />
      </>
    );
  }

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-medium text-foreground">Activity log</h2>
      {body}
    </section>
  );
};

export default SiteDetailEvents;
